<template>
  <div class="mobile-auth-layout">
    <v-dialog v-model="showTokenError" persistent max-width="400">
      <v-card class="text-center pa-6">
        <v-icon size="48" color="error" class="mb-4">mdi-alert-circle</v-icon>
        <v-card-title class="text-h5 justify-center mb-3">
          Session Expired
        </v-card-title>
        <v-card-text class="mb-4">
          {{ tokenError }}
        </v-card-text>
        <v-card-actions class="justify-center">
          <v-btn color="primary" @click="goToLogin" block>
            Back to login
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <div v-if="isAuthenticating" class="loading-screen">
      <div class="loading-content">
        <v-progress-circular indeterminate color="primary" size="64" />
        <p class="mt-4">Authenticating...</p>
      </div>
    </div>

    <template v-else-if="isAuthenticated && !tokenError">
      <div class="mobile-header">
        <div class="header-content" align="center">
          <img
            src="/assets/SDLOGO_40.png"
            alt="StorageDefender"
            srcset="/assets/SDLOGO_sm.png 42w, /assets/SDLOGO_40.png"
            sizes="(max-width: 600px) 42px, 306px"
          />
          <div v-if="currentFacility" class="facility-info">
            <v-chip color="white" text-color="primary" small>
              {{ currentFacility.name }}
            </v-chip>
          </div>

          <div class="session-timer">
            <div class="timer-container">
              <v-icon :color="timerColor" size="16" class="timer-icon ml-2">
                mdi-clock-outline
              </v-icon>
              <span class="mr-2">Session Time </span>

              <span :class="['timer-text']">
                {{ formattedTimeRemaining }}
              </span>
            </div>
            <div class="timer-bar">
              <div
                class="timer-progress"
                :style="{
                  width: progressPercentage + '%',
                  backgroundColor: timerBarColor,
                }"
              ></div>
            </div>
          </div>
        </div>
      </div>

      <div class="mobile-content">
        <slot
          :facility-id="facilityId"
          :current-facility="currentFacility"
          :clear-auth="clearAuth"
        ></slot>
      </div>
    </template>
  </div>
</template>

<script>
import { ref } from "vue";
import { AUTHENTICATE_WITH_QR_TOKEN } from "../../components/authentication/graphql";
import {
  getQRTokenFromUrl,
  getMobileToken,
  isTokenExpired,
} from "../../apollo-client/apollo-token-mobile";

export default {
  name: "MobileAuthLayout",

  setup() {
    const facilityId = ref(null);

    return {
      facilityId,
    };
  },

  data() {
    return {
      isAuthenticating: false,
      tokenError: "",
      currentFacility: null,
      isAuthenticated: false,
      sessionTimer: null,
      sessionStartTime: null,
      sessionDuration: 90 * 60 * 1000,
      timeRemaining: 0,
      timerInterval: null,
    };
  },

  computed: {
    showTokenError() {
      return !!this.tokenError;
    },

    formattedTimeRemaining() {
      const minutes = Math.floor(this.timeRemaining / 60000);
      const seconds = Math.floor((this.timeRemaining % 60000) / 1000);
      return `${minutes.toString().padStart(2, "0")}:${seconds
        .toString()
        .padStart(2, "0")}`;
    },

    progressPercentage() {
      if (this.sessionDuration === 0) return 0;
      return Math.max(0, (this.timeRemaining / this.sessionDuration) * 100);
    },

    timerColor() {
      if (this.timeRemaining <= 5 * 60 * 1000) return "red";
      if (this.timeRemaining <= 10 * 60 * 1000) return "orange";
      return "black";
    },

    timerBarColor() {
      if (this.timeRemaining <= 5 * 60 * 1000) return "#f44336";
      if (this.timeRemaining <= 10 * 60 * 1000) return "#ff9800";
      return "#4caf50";
    },
  },

  beforeUnmount() {
    this.clearSessionTimer();
  },
  async mounted() {
    const sessionRestored = this.restoreSessionTimer();

    if (!sessionRestored) {
      await this.initAuth();
    } else {
      this.isAuthenticated = true;
      const urlParams = new URLSearchParams(window.location.search);
      const facilityId = urlParams.get("facility");
      if (facilityId) {
        this.facilityId = facilityId;
      }
      this.$emit("auth-ready", this.facilityId);
    }
  },
  methods: {
    async initAuth() {
      const authResult = await this.authenticate();
      if (authResult && authResult.facilityId) {
        this.facilityId = authResult.facilityId;
        this.startSessionTimer();
        this.$emit("auth-ready", this.facilityId);
      }
    },
    goToLogin() {
      window.location.href = "/";
    },

    retryAuth() {
      this.tokenError = "";
      this.clearSessionTimer();
      this.initAuth();
    },

    updateSessionTimer() {
      const elapsed = Date.now() - this.sessionStartTime;
      this.timeRemaining = Math.max(0, this.sessionDuration - elapsed);

      if (this.timeRemaining <= 0) {
        this.handleSessionExpired();
      }
    },

    startSessionTimer() {
      this.sessionStartTime = Date.now();
      this.timeRemaining = this.sessionDuration;

      localStorage.setItem(
        "mobile_session_start",
        this.sessionStartTime.toString()
      );
      localStorage.setItem(
        "mobile_session_duration",
        this.sessionDuration.toString()
      );

      this.timerInterval = setInterval(this.updateSessionTimer, 1000);
    },

    clearSessionTimer() {
      if (this.timerInterval) {
        clearInterval(this.timerInterval);
        this.timerInterval = null;
      }
      localStorage.removeItem("mobile_session_start");
      localStorage.removeItem("mobile_session_duration");
    },

    restoreSessionTimer() {
      const sessionStart = localStorage.getItem("mobile_session_start");
      const sessionDuration = localStorage.getItem("mobile_session_duration");

      if (sessionStart && sessionDuration) {
        this.sessionStartTime = parseInt(sessionStart);
        this.sessionDuration = parseInt(sessionDuration);

        const elapsed = Date.now() - this.sessionStartTime;
        this.timeRemaining = Math.max(0, this.sessionDuration - elapsed);

        if (this.timeRemaining <= 0) {
          this.handleSessionExpired();
          return false;
        }

        this.timerInterval = setInterval(this.updateSessionTimer, 1000);

        return true;
      }

      return false;
    },

    handleSessionExpired() {
      this.clearSessionTimer();
      this.tokenError =
        "Your session has expired for security reasons. Please scan the QR code again to continue.";
      this.isAuthenticated = false;
      this.currentFacility = null;
      localStorage.removeItem("mobile_inventory_assignments");
    },

    async authenticate() {
      try {
        let token = getMobileToken();

        if (!token) {
          const qrToken = getQRTokenFromUrl();
          if (!qrToken) {
            this.tokenError =
              "No QR token found. Please scan the QR code again.";
            return false;
          }

          this.isAuthenticating = true;
          try {
            const result = await this.$apollo.mutate({
              mutation: AUTHENTICATE_WITH_QR_TOKEN,
              variables: { qrToken },
            });

            if (result.data?.authenticateWithQrToken?.token) {
              token = result.data.authenticateWithQrToken.token;

              if (result.data.authenticateWithQrToken.organization) {
                this.currentFacility =
                  result.data.authenticateWithQrToken.organization;
              }
            } else {
              throw new Error("Authentication failed - no token received");
            }
          } catch (error) {
            console.error("QR token authentication failed:", error);
            this.tokenError = `Authentication failed: ${error.message}`;
            localStorage.removeItem("mobile_inventory_assignments");
            return false;
          } finally {
            this.isAuthenticating = false;
          }
        }

        if (isTokenExpired(token)) {
          this.tokenError =
            "Authentication token has expired. Please scan the QR code again.";
          return false;
        }

        const urlParams = new URLSearchParams(window.location.search);
        const facilityId = urlParams.get("facility");

        if (!facilityId) {
          this.tokenError =
            "Facility ID is required. Please scan the QR code again.";
          return false;
        }

        this.tokenError = "";
        this.isAuthenticated = true;
        return { facilityId, token };
      } catch (error) {
        console.error("Error during authentication:", error);
        this.tokenError = "Failed to authenticate. Please try again.";
        return false;
      }
    },

    clearAuth() {
      this.isAuthenticated = false;
      this.tokenError = "";
      this.currentFacility = null;
      this.clearSessionTimer();
      localStorage.removeItem("mobile_inventory_assignments");
    },
  },
};
</script>

<style scoped>
.mobile-auth-layout {
  min-height: 100vh;
  background-color: #f5f5f5;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  padding: 0;
  margin: 0;
}

.loading-screen {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.loading-content {
  text-align: center;
}

.mobile-header {
  /* color: white; */
  padding: 16px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.header-content {
  max-width: 1200px;
  margin: 0 auto;
}

.facility-info {
  margin-top: 8px;
}

.session-timer {
  margin-top: 12px;
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  backdrop-filter: blur(10px);
}

.timer-container {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 6px;
}

.timer-icon {
  margin-right: 6px;
}

.timer-bar {
  height: 3px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 2px;
  overflow: hidden;
}

.timer-progress {
  height: 100%;
  transition: width 1s ease, background-color 0.3s ease;
  border-radius: 2px;
}

.mobile-content {
  flex: 1;
}

@media (max-width: 768px) {
  .mobile-header {
    padding: 12px;
  }
}
</style>
